/* One purchase authority: read the live product form and delegate to its submit button. */
if (!customElements.get('opale-sticky-purchase')) {
  customElements.define('opale-sticky-purchase', class extends HTMLElement {
    connectedCallback() {
      this.root = this.closest('product-info');
      this.information = this.root?.querySelector('.op-pdp__information');
      if (!this.information) return;
      this.button = this.querySelector('[data-op-sticky-submit]');
      this.variant = this.querySelector('[data-op-sticky-variant]');
      this.price = this.querySelector('[data-op-sticky-price]');
      this.status = this.querySelector('[data-op-sticky-status]');
      this.controller = new AbortController();
      const signal = this.controller.signal;
      this.mobile = matchMedia('(max-width: 900px)');
      this.schedule = () => {
        if (this.frame) return;
        this.frame = requestAnimationFrame(() => { this.frame = null; this.refresh(); });
      };
      this.observer = new IntersectionObserver(this.schedule, { rootMargin: '-68px 0px 0px 0px', threshold: 0 });
      this.contentObserver = new MutationObserver(this.schedule);
      this.contentObserver.observe(this.information, { subtree: true, childList: true, characterData: true, attributes: true, attributeFilter: ['disabled', 'aria-disabled', 'class', 'value', 'hidden'] });
      // Shopify may insert its consent UI after the product initializes.
      this.consentSelector = '#shopify-pc__banner, #shopify-pc__prefs, [role="alertdialog"][aria-label="Cookie consent"]';
      this.consentObserver = new MutationObserver(records => {
        if (records.some(record => !this.contains(record.target) && (record.target.matches?.(this.consentSelector) || record.target.closest?.(this.consentSelector) || record.target.querySelector?.(this.consentSelector) || [...(record.addedNodes || []), ...(record.removedNodes || [])].some(node => node.matches?.(this.consentSelector) || node.querySelector?.(this.consentSelector))))) this.schedule();
      });
      this.consentObserver.observe(document.body, { subtree: true, childList: true, attributes: true, attributeFilter: ['class', 'style', 'hidden', 'aria-hidden'] });
      this.information.addEventListener('change', event => {
        if (event.target.closest('variant-selects')) this.choiceConfirmed = true;
        this.schedule();
      }, { signal });
      this.information.addEventListener('click', event => {
        // Clicking an already-selected radio is still an explicit size choice.
        const target = event.target;
        if (target.closest('variant-selects') && (target.matches('input[type="radio"]') || target.closest('label')?.control?.type === 'radio')) this.choiceConfirmed = true;
        this.schedule();
      }, { signal });
      this.button.addEventListener('click', () => this.purchase(), { signal });
      window.addEventListener('resize', this.schedule, { passive: true, signal });
      // Covers fast scroll jumps that skip an IntersectionObserver intersection.
      window.addEventListener('scroll', this.schedule, { passive: true, signal });
      document.addEventListener('visitorConsentCollected', this.schedule, { signal });
      this.mobile.addEventListener('change', this.schedule, { signal });
      this.refresh();
    }
    get originalButton() { return this.information.querySelector('product-form [type="submit"]'); }
    get requiresChoice() { return !!this.information.querySelector('variant-selects') && !this.choiceConfirmed; }
    refresh() {
      const original = this.originalButton;
      if (!original) { this.hidden = true; return; }
      if (this.observedButton !== original) {
        if (this.observedButton) this.observer.unobserve(this.observedButton);
        this.observedButton = original;
        this.observer.observe(original);
      }
      const consentVisible = Array.from(document.querySelectorAll(this.consentSelector)).some(element => element.getClientRects().length && getComputedStyle(element).visibility !== 'hidden' && getComputedStyle(element).opacity !== '0');
      this.hidden = !this.mobile.matches || original.getBoundingClientRect().bottom > 68 || consentVisible;
      const loading = original.classList.contains('loading');
      const unavailable = original.disabled || original.getAttribute('aria-disabled') === 'true';
      this.button.disabled = unavailable;
      this.button.setAttribute('aria-busy', String(loading));
      const originalLabel = original.querySelector('span:not(.hidden)')?.textContent.trim() || original.textContent.trim();
      this.setText(this.button, loading ? this.dataset.loadingLabel : unavailable ? originalLabel : this.requiresChoice ? this.dataset.chooseLabel : originalLabel);
      this.button.setAttribute('aria-label', `${this.button.textContent}: ${this.querySelector('.op-sticky-purchase__title').textContent}`);
      const selected = Array.from(this.information.querySelectorAll('variant-selects input:checked, variant-selects select')).map(input => input.tagName === 'SELECT' ? input.selectedOptions[0]?.textContent.trim() : input.value).filter(Boolean);
      this.setText(this.variant, selected.join(' / '));
      const pricing = this.information.querySelector('[id^="price-"] .price');
      this.setText(this.price, pricing?.querySelector(pricing.classList.contains('price--on-sale') ? '.price-item--sale' : '.price-item--regular')?.textContent.trim() || '');
      const error = this.information.querySelector('.product-form__error-message-wrapper');
      this.setText(this.status, error && !error.hidden ? error.querySelector('.product-form__error-message')?.textContent.trim() || '' : '');
    }
    setText(element, text) { if (element.textContent !== text) element.textContent = text; }
    purchase() {
      // Re-read immediately before submission: never cache a variant ID or cart payload.
      const original = this.originalButton;
      if (!original || original.disabled || original.getAttribute('aria-disabled') === 'true') return;
      if (this.requiresChoice) {
        const selector = this.information.querySelector('variant-selects');
        selector.scrollIntoView({ block: 'center', behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
        selector.querySelector('input:checked, select, input')?.focus({ preventScroll: true });
        return;
      }
      original.click();
      this.refresh();
    }
    disconnectedCallback() {
      this.controller?.abort(); this.observer?.disconnect(); this.contentObserver?.disconnect(); this.consentObserver?.disconnect();
      if (this.frame) cancelAnimationFrame(this.frame);
    }
  });
}
